import { PermissionsEnum } from '@/auth/interfaces';
import { useFetchAgendamientos, useFetchMotivos } from '@/shared/actions/erp';
import { GeneralTable, TableTabsScene } from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { Agendamiento, EstadoBitacoraEnum, EstadoMotivoEnum, IDataBitacoraRespById, Venta } from '@/shared/interfaces';
import { hasPermission } from '@/shared/utils';
import { useColumnsAgendamientos } from '../../shared/hooks';
import { EstadosAgendamientoEnumChoice, EstadosContratoChoiceEnum, ModulosEnumChoice, ModulosMotivoENUM, ROUTER_PATHS, TABLE_CONSTANTS_COLUMS, TipoParametroChoiceEnum } from '@/shared';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useDiccionarioMotivo } from '@/store/erp/diccionario_motivo/diccionario_motivo.store';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ModalMotivoRechazo from '@/shared/components/ui/ModalMotivoRechazo/ModalMotivoRechazo';
import { useModalMotivoRechazo } from '@/shared/components/ui/ModalMotivoRechazo/useModalMotivoRechazo';
import { useNavigate } from 'react-router-dom';
import { Edit } from '@mui/icons-material';

export type AgendasPendingRecoordinacionPyLPageProps = {
  enableActions?: boolean;
  estado_agendamiento?: string;
  // 'exclude' (default) = comportamiento actual (excluye TRASLADO -> tabs de
  // instalaciones intactos). 'only' = muestra SOLO traslados (tabs nuevos).
  tipoServicioMode?: 'exclude' | 'only';
};

const AgendasPendingRecoordinacionPyLPage: React.FC<
  AgendasPendingRecoordinacionPyLPageProps
> = ({
  enableActions = true,
  estado_agendamiento,
  tipoServicioMode = 'exclude'
}) => {
    const esSoloTraslados = tipoServicioMode === 'only';
    useCheckPermissions(PermissionsEnum.api_view_agendamiento);
    const returnUrlAgendamientosPyLPage =
      ROUTER_PATHS.operaciones.agendamientoPyLNav;
    const navigate = useNavigate();


    const {
      setOpen,
      socket,
      departamento: depSocket,
      setOpenCreaBitacora,
      setDetalle,
      setFaseVenta,
      setDataGetVentaById,
    } = useDiccionarioMotivo()

    const user = useAuthStore(s => s.user);
    const setAlert = useUiStore(s => s.setAlert);

    const { data: dataMotivos } = useFetchMotivos({
      estado: true,
      modulo_in: ModulosMotivoENUM.BITACORA_PYL,
      page_size: 256,
    });

    const useHookModalMotivoRechazo = useModalMotivoRechazo({
      modulo: ModulosEnumChoice.AGENDAMIENTO
    })

    // server side filters - colums table
    const { filterObject, columnFilters, setColumnFilters } =
      useTableServerSideFiltering();

    ///* table
    const {
      globalFilter,
      pagination,
      searchTerm,
      onChangeFilter,
      setPagination,
      cleanFilter
    } = useTableFilter();
    const { pageIndex, pageSize } = pagination;

    ///* fetch data
    const {
      data: agendamientosPagingRes,
      isLoading,
      isRefetching,
    } = useFetchAgendamientos({
      enabled: true,
      params: {
        page: pageIndex + 1,
        page_size: pageSize,
        identificacion: searchTerm,
        ...filterObject,
        // 'only' -> incluye SOLO traslados; default -> excluye TRASLADO (igual que antes).
        ...(esSoloTraslados
          ? { tipo_orden_servicio: 'TRASLADO' }
          : { tipo_orden_servicio_exclude: 'TRASLADO' }),
        filterByState: false,
        // Instalaciones: excluir contratos ACTIVO (contratos nuevos). Traslados: NO
        // excluir, porque un traslado SIEMPRE está sobre un contrato ACTIVO (si se
        // excluye, nunca aparecería en los tabs de traslados).
        contrato_estado_contrato_exclude: esSoloTraslados
          ? undefined
          : EstadosContratoChoiceEnum.ACTIVO,
        estado_agendamiento_in: estado_agendamiento ||
          `${EstadosAgendamientoEnumChoice.RECORDINADO},${EstadosAgendamientoEnumChoice.PENDIENTE_RECORDINACION}`,
      },
    });

    ///* columns
    const { agendamientosRecoordinados, agendamientosTraslados } = useColumnsAgendamientos();
    const columnasTabla = esSoloTraslados ? agendamientosTraslados : agendamientosRecoordinados;



    return (
      <>
        <TableTabsScene
          globalFilter={globalFilter}
          onChangeFilter={onChangeFilter}
          searchInputText="por identificación"
          //onChangeRangeDate={onChangeRangeDate}
          cleanFilter={cleanFilter}
        >
          <GeneralTable<Agendamiento>
            columns={columnasTabla}
            data={agendamientosPagingRes?.data?.data || []}
            isLoading={isLoading}
            isRefetching={isRefetching}
            // // filters - server side
            enableManualFiltering={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            // // search
            enableGlobalFilter={false}
            paintRow={(row)=>{
              if(
                row?.estado_llamada == "NO REALIZADA" 
                && JSON.stringify(row?.observaciones_x)?.includes("RE_COORDINAR") 
              ){
                return "#FFFA94"  
              }
              return ""
            }}
            // // pagination
            pagination={pagination}
            onPaging={setPagination}
            rowCount={agendamientosPagingRes?.data?.count}
            // // actions ========
            enableColumnActions={hasPermission(
              PermissionsEnum.api_change_agendamiento
            )}
            // crud
            positionActionsColumn="first"
            canEdit={false}
            //enableEditing={false}
            showCustomActionBtnSpace={true}
            enableEditing={enableActions}
            actionsColumnSize={TABLE_CONSTANTS_COLUMS.COLUMN_WIDTH_SMALL}
            customActionBtnSpace={(row) => {
              return (
                <Grid container>
                  <Grid item>
                    <Tooltip title='Bitácora'>
                      <IconButton
                        onClick={() => {
                          console.log('[BITACORA-OPS] Click button. observaciones_x:', row.observaciones_x);
                          if (!socket) {
                            console.warn('[BITACORA-OPS] socket is null');
                            setAlert(true, 'Conexión de bitácora no disponible. Recarga la página.', AlertSeverityEnum.error);
                            return;
                          }

                          const idPreagendamiento = row.preagendamiento_data?.id_preagendamiento;
                          console.log('[BITACORA-OPS] idPreagendamiento:', idPreagendamiento);
                          if (!idPreagendamiento) {
                            console.warn('[BITACORA-OPS] No idPreagendamiento');
                            setAlert(true, 'No se encontró el preagendamiento de esta fila.', AlertSeverityEnum.error);
                            return;
                          }

                          // Construir detalle para fallback al modal manual de creación
                          const observacionTecnico = row.observaciones_x?.observaciones?.observacion_rechazo_tecnico || '';
                          const motivoRechazoTecnico = row.observaciones_x?.motivo_rechazo || '';
                          const tipoNovedad = row.observaciones_x?.tipo_novedad || '';
                          const detalleFallback = {
                            numero_referencia_venta: idPreagendamiento,
                            identificacion_cliente: row.prospecto_data?.identificacion,
                            nombre_cliente: row.prospecto_data?.razon_social,
                            canal_venta: row.prospecto_data?.canal_venta_create,
                            vendedor: row.prospecto_data?.user_create,
                            fecha_aceptacion: row.preventa_data?.fecha_aceptacion,
                            // Campos extra para el pre-fill del BitacoraDialog
                            observacion_rechazo_tecnico: observacionTecnico,
                            motivo_rechazo_tecnico: motivoRechazoTecnico,
                            tipo_novedad: tipoNovedad,
                            estado_agendamiento: row.estado_agendamiento,
                          };

                          const abrirCreacionManual = () => {
                            setDetalle(detalleFallback);
                            setFaseVenta(ModulosEnumChoice.RECORDINADO);
                            setOpenCreaBitacora(true);
                          };

                          // Indicador confiable de origen técnico: el backend Django
                          // (OrdenTrabajoDetailView.metodo_extencion_put) setea
                          // observaciones_x.tipo_novedad SOLO cuando el técnico
                          // reporta una novedad. Otros orígenes no lo tienen.
                          const esOriginadoPorTecnico = !!tipoNovedad;
                          console.log('[BITACORA-OPS] tipoNovedad:', tipoNovedad, '| esOriginadoPorTecnico:', esOriginadoPorTecnico);

                          console.log('[BITACORA-OPS] Emitting getVentaById...');
                          socket.emit('getVentaById', ({
                            numero_referencia_venta: idPreagendamiento
                          }), (data: IDataBitacoraRespById) => {
                            console.log('[BITACORA-OPS] getVentaById response:', data);
                            if (data?.code == 200 && data.data) {
                              // Bitácora ya existe → poblar el store directamente con
                              // los datos que ya tenemos en la respuesta y abrir el modal.
                              // Esto evita la race condition de hacer un segundo
                              // emitGetVentaById async (que dejaba el modal en blanco
                              // hasta que respondiera, o no aparecía si fallaba).
                              console.log('[BITACORA-OPS] Bitacora exists, setting data and opening read view');
                              setDataGetVentaById(data.data);
                              setOpen(true);
                              return;
                            }

                            // Bitácora no existe → decidir flujo según origen
                            if (!esOriginadoPorTecnico) {
                              // Origen NO técnico → comportamiento prod: abrir modal manual de creación
                              // (no auto-crear bitácora con datos del técnico que no existen)
                              console.log('[BITACORA-OPS] Bitacora does NOT exist and origen NOT tecnico → opening MANUAL modal');
                              abrirCreacionManual();
                              return;
                            }
                            console.log('[BITACORA-OPS] Bitacora does NOT exist, origen IS tecnico → AUTO-CREATING');

                            // Origen técnico → intentar auto-crear con datos del rechazo
                            const esRecoordinacion =
                              tipoNovedad === 'RE_COORDINAR' ||
                              row.estado_agendamiento === 'RECORDINADO';
                            const motivoCodigo = esRecoordinacion
                              ? 'RECORDINADO_PLANTA_EXTERNA_PYL'
                              : 'FALLIDO_PLANTA_EXTERNA_PYL';

                            const motivoEncontrado = dataMotivos?.data?.data?.find(
                              (m: any) => m.codigo === motivoCodigo && m.modulo === depSocket
                            );
                            if (!motivoEncontrado) {
                              // Fallback: abrir modal manual de creación (comportamiento prod)
                              abrirCreacionManual();
                              return;
                            }

                            const _textoComentarioBase = motivoRechazoTecnico
                              ? `Motivo: ${motivoRechazoTecnico} - ${observacionTecnico}`
                              : observacionTecnico;
                            // En los tabs de traslados, etiquetar la bitácora como TRASLADO.
                            const textoComentario = esSoloTraslados
                              ? `TRASLADO — ${_textoComentarioBase}`
                              : _textoComentarioBase;

                            // Info del técnico que reportó (guardada en observaciones_x por el backend)
                            const tecnicoUsername = row.observaciones_x?.tecnico_username ?? '';
                            const tecnicoRol = row.observaciones_x?.tecnico_rol ?? '';
                            const tecnicoDepartamento = row.observaciones_x?.tecnico_departamento_nombre ?? 'TECNICO';

                            // URLs de las evidencias del técnico (guardadas en
                            // OrdenTrabajo.urls_fotos_rechazo_tecnico al reportar la novedad)
                            const imageUrlsTecnico: string[] = (row.observaciones_x?.urls_fotos_rechazo_tecnico ?? [])
                              .map((f: any) => f?.photoUrl)
                              .filter((u: any): u is string => !!u);

                            const ventaData = {
                              numero_referencia_venta: idPreagendamiento,
                              identificacion_cliente: row.prospecto_data?.identificacion,
                              nombre_cliente: row.prospecto_data?.razon_social,
                              canal_venta: row.prospecto_data?.canal_venta_create,
                              vendedor: row.prospecto_data?.user_create,
                              fecha_aceptacion: row.preventa_data?.fecha_aceptacion,
                              creado_por: (tecnicoUsername || user?.username) ?? '',
                              fase_venta: ModulosEnumChoice.RECORDINADO,
                              motivos: [{
                                numero_referencia_motivo: motivoEncontrado.id_motivo,
                                descripcion: motivoEncontrado.nombre,
                                prioridad: motivoEncontrado.prioridad || 'ALTA',
                                estado_motivo: EstadoMotivoEnum.NO_RESUELTO,
                                modulo: motivoEncontrado.modulo,
                                comentarios: [{
                                  comentario: textoComentario || 'Novedad reportada por técnico',
                                  username: (tecnicoUsername || user?.username) ?? '',
                                  rol: (tecnicoRol || user?.rol) ?? '',
                                  departamento: tecnicoDepartamento,
                                  tipo: TipoParametroChoiceEnum.TEXTO,
                                }]
                              }]
                            };

                            // Flujo original: crear nueva bitácora ligada a este preagendamiento.
                            const crearNuevaBitacora = () => {
                              socket.emit('createVenta', { ventaData }, (createResponse: IDataBitacoraRespById) => {
                                if (createResponse?.code != 200) {
                                  setAlert(true, 'No se pudo auto-crear la bitácora. Abriendo formulario manual.', AlertSeverityEnum.warning);
                                  abrirCreacionManual();
                                  return;
                                }
                                setAlert(true, createResponse.message, AlertSeverityEnum.success);

                                setTimeout(() => {
                                  socket.emit('getVentaById', {
                                    numero_referencia_venta: idPreagendamiento
                                  }, (getResponse: IDataBitacoraRespById) => {
                                    if (getResponse?.code != 200 || !getResponse.data) {
                                      setOpen(true);
                                      return;
                                    }
                                    const venta = getResponse.data;
                                    const dm = venta.diccionario_motivo?.[0];
                                    const motivo = dm?.motivos?.[0];

                                    if (dm && motivo && imageUrlsTecnico.length > 0) {
                                      imageUrlsTecnico.forEach(url => {
                                        socket.emit('enviarComentario', {
                                          numero_referencia_venta: venta.numero_referencia_venta,
                                          diccionario_motivo_id: dm._id,
                                          motivo_id: motivo._id,
                                          comentario_texto: url,
                                          username_create_venta: (tecnicoUsername || user?.username) ?? '',
                                          tipo: TipoParametroChoiceEnum.IMAGE,
                                          departamento: tecnicoDepartamento,
                                          username: (tecnicoUsername || user?.username) ?? '',
                                          rol: (tecnicoRol || user?.rol) ?? '',
                                        });
                                      });
                                    }

                                    setDataGetVentaById(venta);
                                    setOpen(true);
                                  });
                                }, 200);
                              });
                            };

                            // Antes de crear, buscar bitácoras existentes del mismo cliente
                            // (por identificacion_cliente) para evitar duplicar el registro
                            // en /operaciones/bitacora. Si existe al menos una activa con un
                            // motivo NO_RESUELTO, agregamos el reporte del técnico como
                            // comentario cronológico a ese motivo.
                            const identificacionCliente = row.prospecto_data?.identificacion;

                            if (!identificacionCliente) {
                              crearNuevaBitacora();
                              return;
                            }

                            let resueltoFiltro = false;
                            const handlerFiltro = (response: {
                              code?: number;
                              data?: { data?: Venta[] };
                            }) => {
                              if (resueltoFiltro) return;
                              if (response?.code !== 200) return;
                              const ventasAll = Array.isArray(response?.data?.data)
                                ? response.data.data
                                : [];
                              const bitacorasCliente = ventasAll.filter(
                                v => v.identificacion_cliente === identificacionCliente,
                              );
                              resueltoFiltro = true;
                              socket.off('getVentas', handlerFiltro);

                              if (bitacorasCliente.length === 0) {
                                crearNuevaBitacora();
                                return;
                              }

                              // Solo lectura: el comentario del técnico ya lo emitió
                              // RejectInstalacionAsigTecModal al reportar novedad. Re-emitir
                              // aquí duplica el mensaje. Por eso solo abrimos la bitácora
                              // activa más reciente del cliente.
                              const bitacoraExistente = bitacorasCliente
                                .filter(v =>
                                  v.estado_bitacora === EstadoBitacoraEnum.EN_PROCESO &&
                                  v.diccionario_motivo?.some(dm =>
                                    dm.motivos?.some(
                                      m => m.estado_motivo === EstadoMotivoEnum.NO_RESUELTO,
                                    ),
                                  ),
                                )
                                .sort(
                                  (a, b) =>
                                    new Date(b.updatedAt).getTime() -
                                    new Date(a.updatedAt).getTime(),
                                )[0];

                              if (!bitacoraExistente) {
                                crearNuevaBitacora();
                                return;
                              }

                              setDataGetVentaById(bitacoraExistente);
                              setOpen(true);
                            };

                            socket.on('getVentas', handlerFiltro);
                            socket.emit('getVentasFilter', {
                              page: 1,
                              pageSize: 100,
                              filtros: { identificacion_cliente: identificacionCliente },
                            });
                            setTimeout(() => {
                              if (!resueltoFiltro) {
                                resueltoFiltro = true;
                                socket.off('getVentas', handlerFiltro);
                                crearNuevaBitacora();
                              }
                            }, 3000);
                          })
                        }}
                      >
                        <ForumRoundedIcon />
                      </IconButton>

                    </Tooltip>

                  </Grid>

                  <Grid item>
                    <Tooltip title={"Editar"} placement="top">
                      <IconButton
                        onClick={() => {
                          // Traslados: van al detalle de TRASLADO (ACTUALIZAR HORARIOS
                          // por zona + Recoordinar + Observaciones Recoordinar), NO al
                          // reasignar de instalación. Guardado por esSoloTraslados.
                          if (esSoloTraslados) {
                            navigate(
                              `${ROUTER_PATHS.operaciones.trasladosAgendamientoPyLNav}/${row.id_agendamiento}`
                            );
                            return;
                          }

                          navigate(
                            `${returnUrlAgendamientosPyLPage}/reasignar/${row.id_agendamiento}`
                          );
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                </Grid>
              )
            }}
          />
        </TableTabsScene>

        {/*Modal para cancelar*/}
        <ModalMotivoRechazo
          {...useHookModalMotivoRechazo}
        />
      </>

    );
  };

export default AgendasPendingRecoordinacionPyLPage;
